import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { ShoppingCart, ChevronDown, Tag } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import AdminQuickQRButton from "@/components/AdminQuickQRButton";
import AuthDialog from "@/components/AuthDialog";
import BackButton from "@/components/BackButton";
import NavbarUserMenu from "@/components/NavbarUserMenu";
import NavbarMobileMenu from "@/components/NavbarMobileMenu";
import NavbarCategorySearch from "@/components/NavbarCategorySearch";

/**
 * Nav is a mix of flat links and dropdown groups.
 * Groups render a hover/click dropdown on desktop with `children`; mobile
 * flattens groups to show every child on a single sheet.
 */
const NAV_ITEMS = [
  {
    label: "Home",
    href: "/",
    children: [
      { label: "Home", href: "/" },
      { label: "About Us", href: "/about" },
    ],
  },
  {
    label: "Directory",
    href: "/directory",
    children: [
      { label: "Directory", href: "/directory" },
      { label: "By State", href: "/states" },
      { label: "Featured", href: "/featured" },
      { label: "Founding Business ($79.99)", href: "/founding-business" },
      { label: "Founding 100 Wall", href: "/founding-100" },
    ],
  },
  { label: "Blinterest", href: "/blinterest" },
  { label: "News", href: "/news" },
  { label: "Membership", href: "/pricing" },
];

// Flat list for the mobile sheet menu — expand groups into their children.
const MOBILE_LINKS = NAV_ITEMS.flatMap((item) => item.children || [item]);

export default function Navbar() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!user) { setCartCount(0); return; }
    const fetchCartCount = async () => {
      try {
        const response = await axios.get(`${API}/cart`, { withCredentials: true });
        const items = response.data.cart?.items || [];
        setCartCount(items.reduce((sum, item) => sum + item.quantity, 0));
      } catch {
        setCartCount(0);
      }
    };
    fetchCartCount();
    // Cart-count pub/sub: any component that mutates the cart fires
    // `cart:updated` on window and we refetch.
    const onCartUpdated = () => fetchCartCount();
    window.addEventListener("cart:updated", onCartUpdated);
    return () => window.removeEventListener("cart:updated", onCartUpdated);
  }, [user, location]);

  // Fetch active categories once for the "Search by category" menu.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/businesses/categories/active`);
        if (!cancelled) setCategories(r.data?.categories || []);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <nav
      className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/80 backdrop-blur-xl"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Universal Back button — hidden on landing/root routes. */}
          <div className="flex items-center gap-1 min-w-0 flex-1">
            <BackButton className="flex-shrink-0" />
            {/* Logo — TBC monogram + wordmark. Wordmark hidden below xl. */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="navbar-logo">
              <img
                src="/icon-192.png?v=3"
                alt="The Blk Community"
                className="h-10 sm:h-12 w-10 sm:w-12 rounded-lg flex-shrink-0"
              />
              <span className="h-8 sm:h-10 w-px bg-gray-300/70 hidden xl:block" aria-hidden />
              <span className="hidden xl:inline font-heading text-xl 2xl:text-2xl text-tbc-navy tracking-tight leading-none whitespace-nowrap">
                The<span className="text-tbc-gold">Blk</span>Community<span>.com</span>
              </span>
              <span className="inline xl:hidden font-heading text-base sm:text-lg text-tbc-navy tracking-tight leading-none whitespace-nowrap truncate">
                The<span className="text-tbc-gold">Blk</span>Community
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {NAV_ITEMS.map((item) => {
              const isActive =
                item.href === location.pathname ||
                item.children?.some((c) => c.href === location.pathname);
              const cls = `text-sm font-medium transition-colors flex items-center gap-1 ${
                isActive ? "text-tbc-navy" : "text-gray-600 hover:text-tbc-navy"
              }`;
              if (item.children) {
                const withCategoryMenu = item.label === "Directory" && categories.length > 0;
                return (
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger
                      className={`${cls} focus:outline-none`}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                      <ChevronDown className="w-3.5 h-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[10rem]">
                      {item.children.map((child) => (
                        <DropdownMenuItem key={child.href} asChild>
                          <Link
                            to={child.href}
                            className="w-full cursor-pointer"
                            data-testid={`nav-child-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {child.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                      {withCategoryMenu && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger
                              className="cursor-pointer"
                              data-testid="nav-directory-by-category"
                            >
                              <Tag className="w-3.5 h-3.5 mr-2" />
                              Search by category
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="min-w-[16rem] max-h-[70vh] overflow-y-auto">
                              <NavbarCategorySearch categories={categories} variant="desktop" />
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cls}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            <AdminQuickQRButton />
            {user && <NotificationBell />}
            {user && (
              <Link
                to="/cart"
                className="relative p-2 text-gray-600 hover:text-tbc-navy transition-colors"
                data-testid="cart-link"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 w-5 h-5 bg-tbc-gold text-tbc-navy text-xs font-bold rounded-full flex items-center justify-center"
                    data-testid="cart-badge"
                  >
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </Link>
            )}

            {user ? (
              <NavbarUserMenu user={user} loading={loading} onLogout={logout} />
            ) : loading ? (
              <NavbarUserMenu user={null} loading onLogout={logout} />
            ) : (
              <Button
                onClick={() => setAuthOpen(true)}
                className="hidden md:flex bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-6"
                data-testid="login-button"
              >
                Sign In
              </Button>
            )}

            <NavbarMobileMenu
              open={mobileMenuOpen}
              onOpenChange={setMobileMenuOpen}
              mobileLinks={MOBILE_LINKS}
              categories={categories}
              user={user}
              onLogout={logout}
              onOpenAuth={() => setAuthOpen(true)}
            />
          </div>
        </div>
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </nav>
  );
}
